import React, {useEffect} from 'react';
import {observer} from 'mobx-react-lite';
import {Container, Spinner, Button, Form, Dropdown} from 'react-bootstrap';
import {useHistory} from 'react-router-dom';
import {Helmet} from 'react-helmet';
import Navbar from '../../components/Navbar';
import Sidebar from '../../components/Sidebar';
import Footer from '../../components/Footer';
import CourseCard from './Components/CourseCard';
import Filter from './Components/CourseFilter';
import CourseCarousel from './Components/CourseCarousel';
import {Main, CourseFilter, ChipText} from './elements/courses';
import {PaginationContainer} from '../Bookmarks/elements/styles';
import close from './elements/close-chip.png';
import {useStore} from '../../store';
import AlertComponent from '../../components/AlertComponent';
import {amplitude} from '../../utils/Amplitude';
import PageBottom from '../../components/PageBottom';

// Courses page
const Courses = observer(() => {
  const {courseStore, alertStore} = useStore();
  const location = useHistory();
  const {
    professionsList,
    listProfessions,
    courseSpecialtiesList,
    listCourseSpecialties,
    courseHeros,
    fetchHeros,
    coursesList,
    loading,
    selectedProfessionList,
    onRemoveProfession,
    selectedSpecialtiesList,
    selectedProfessions,
    selectedSpecialties,
    sortBy,
    sortOrder,
    isCme,
    courseCount,
    page,
    count,
    pageIncrement,
    pageDecrement,
    changeCount,
    listCourses,
    onRemoveSpecialty,
    onCreateBookmark,
    onDeleteBookmark,
    onFollowCourse,
    onUnfollowCourse,
    playlist,
    getAllPlaylists,
  } = courseStore;

  const {message, status, resetAlert} = alertStore;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    getAllPlaylists();
  }, [getAllPlaylists]);

  useEffect(() => {
    listProfessions();
    listCourseSpecialties();
  }, [listProfessions, listCourseSpecialties]);

  useEffect(() => {
    fetchHeros();
  }, [fetchHeros]);

  useEffect(() => {
    listCourses();
  }, [selectedProfessions, selectedSpecialties, sortBy, sortOrder, isCme, page, count, listCourses]);

  const removeProfession = item => {
    onRemoveProfession(item.id);
  };

  const removeSpecialty = item => {
    onRemoveSpecialty(item.id);
  };

  const onHandleFollow = item => {
    const user = localStorage.getItem('user');
    if (user) {
      if (item.follow?.length) {
        onUnfollowCourse(item);
        const eventProperties = {
          type: 'course',
          source: 'course page',
          name: `${item.title}`,
        };
        amplitude.getInstance().logEvent('unfollow', eventProperties);
      } else {
        onFollowCourse(item.id);
        const eventProperties = {
          type: 'course',
          source: 'course page',
          name: `${item.title}`,
        };
        amplitude.getInstance().logEvent('follow', eventProperties);
      }
    } else {
      location.push('/login');
    }
  };

  const onHandleBookmark = item => {
    const user = localStorage.getItem('user');
    if (user) {
      if (item.bookmark?.length) {
        onDeleteBookmark(item);
      } else {
        onCreateBookmark(item.id);
      }
    } else {
      location.push('/login');
    }
  };

  return (
    <div id="courses" className="d-flex flex-column flex-fill">
      <Helmet
        title="GIBLIB | Courses"
        meta={[
          {
            name: 'description',
            content:
              'Access curated medical courses from the leading academic medical institutions of all topics and specialties.',
          },
          {
            property: 'og:title',
            content: 'GIBLIB | Courses',
          },
          {
            name: 'twitter:card',
            content: 'summary',
          },
          {
            name: 'twitter:image',
            content: 'https://s3.us-west-1.amazonaws.com/v4.0-assets/GBLB_Logo_Horizontal_Notag_White_FullColor.png',
          },
          {
            name: 'image',
            property: 'og:image',
            content: 'https://s3.us-west-1.amazonaws.com/v4.0-assets/GBLB_Logo_Horizontal_Notag_White_FullColor.png',
          },
          {
            property: 'og:url',
            content: window.location.origin + window.location.pathname,
          },
        ]}
      />
      <Navbar />
      <div className="d-flex">
        <Sidebar />
        {message ? <AlertComponent message={message} status={status} click={() => resetAlert()} /> : null}
        <Main role="main" className="w-100">
          <CourseCarousel course={courseHeros} />
          <CourseFilter>
            <Filter professionFilter={professionsList} specialityFilter={courseSpecialtiesList} />
          </CourseFilter>
          <div className="courseBorder-top">
            <div className="d-flex container mt-2 flex-wrap">
              {selectedProfessionList.map(item => (
                <ChipText>
                  {item.name}
                  <span>
                    <button type="button" className="bg-transparent border-0" onClick={() => removeProfession(item)}>
                      <img alt="close" src={close} />
                    </button>
                  </span>
                </ChipText>
              ))}
              {selectedSpecialtiesList.map(item => (
                <ChipText>
                  {item.name}
                  <span>
                    <button type="button" className="bg-transparent border-0" onClick={() => removeSpecialty(item)}>
                      <img alt="close" src={close} />
                    </button>
                  </span>
                </ChipText>
              ))}
            </div>
            <Container id="course_container">
              <div className="py-3 courseBorder-bottom d-flex justify-content-between">
                <span>
                  {courseCount} {courseCount === 1 ? 'Result' : 'Results'}
                </span>
                <PaginationContainer className="text-right">
                  <span>
                    {courseCount === 0 ? <>0 </> : <>{page * count - (count - 1)} </>}-{' '}
                    {page * count > courseCount ? <>{courseCount} </> : <>{page * count} </>}
                    of {courseCount}
                  </span>
                  <Button
                    onClick={pageDecrement}
                    variant="basic"
                    className="a_p_dec"
                    disabled={page * count - (count - 1) === 1}
                  >
                    <i className="fas fa-angle-left" />
                  </Button>
                  <Button
                    onClick={pageIncrement}
                    variant="basic"
                    className="a_p_inc"
                    disabled={page * count >= courseCount}
                  >
                    <i className="fas fa-angle-right" />
                  </Button>
                  <Dropdown>
                    <Dropdown.Toggle variant="basic" id="limitMenu" className="a_limit">
                      <i className="fas fa-ellipsis-v" />
                    </Dropdown.Toggle>

                    <Dropdown.Menu onChange={e => changeCount(e.target.value)}>
                      <Form.Check
                        type="radio"
                        label="10 per page"
                        id="limit_10"
                        value="10"
                        name="count"
                        checked={parseInt(count, 10) === 10}
                      />
                      <Form.Check
                        type="radio"
                        label="25 per page"
                        id="limit_25"
                        value="25"
                        name="count"
                        checked={parseInt(count, 10) === 25}
                      />
                      <Form.Check
                        type="radio"
                        label="50 per page"
                        id="limit_50"
                        value="50"
                        name="count"
                        checked={parseInt(count, 10) === 50}
                      />
                      <Form.Check
                        type="radio"
                        label="100 per page"
                        id="limit_100"
                        value="100"
                        name="count"
                        checked={parseInt(count, 10) === 100}
                      />
                    </Dropdown.Menu>
                  </Dropdown>
                </PaginationContainer>
              </div>
              {loading ? (
                <div className="d-flex justify-content-center mb-5">
                  <Spinner animation="grow" />
                </div>
              ) : (
                coursesList.map(course => (
                  <CourseCard
                    key={course.id}
                    course={course}
                    playlist={playlist}
                    onFollow={onHandleFollow}
                    onBookmark={onHandleBookmark}
                    playlistStore="courseStore"
                  />
                ))
              )}
            </Container>
          </div>
          {!loading && <Footer />}
        </Main>
      </div>
      <PageBottom />
    </div>
  );
});

export default Courses;
